import { useMemo } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { IconButton, Text, UserAvatarsStack, colors, spacing } from '@pletly/ui';
import { useToggle } from '@pletly/common';
import { useAuthHeaders } from '@pletly/feature-auth';
import { getName } from '@pletly/shared';
import { TeamInfoProps } from './types';
import { useTeamPress } from '../useTeamPress';
import { useOrganisationInfo } from '../../../context';
import ChevronIcon from '../chevron-down.svg';

export const TeamInfo = ({ team, activeUuid, style }: TeamInfoProps) => {
	const [expanded, toggleExpanded] = useToggle(false);
	const { getHeaders } = useAuthHeaders();
	const { organisationInfo } = useOrganisationInfo();
	const handleTeamPress = useTeamPress(organisationInfo?.uuid ?? '');
	const active = activeUuid === team.uuid;

	const avatarItems = useMemo(() => {
		if (!team.members.caregivers?.length) {
			return [
				{
					name: team.name,
				},
			];
		}

		return team.members.caregivers.map(({ photoUrl, ...member }) => ({
			name: getName(member),
			photoSource: photoUrl ? { uri: photoUrl, headers: getHeaders(photoUrl) } : undefined,
		}));
	}, [getHeaders, team.members.caregivers, team.name]);

	const hasSubTeams = team.subTeams?.length > 0;

	return (
		<View style={style}>
			<View style={[styles.mainContainer, styles.flexRow, active && styles.active]}>
				<Pressable style={styles.flexRow} onPress={handleTeamPress(team.uuid)}>
					<UserAvatarsStack items={avatarItems} maxAvatars={2} avatarSize={32} withNotShownCount />
					<Text category="p2" style={avatarItems.length < 2 && styles.title}>
						{team.name} {hasSubTeams ? `(${team.subTeams.length})` : ''}
					</Text>
				</Pressable>
				{hasSubTeams && (
					<IconButton
						size="small"
						Icon={() => <ChevronIcon rotation={expanded ? 180 : 0} />}
						onPress={toggleExpanded}
						appearance="ghost"
						style={styles.expandButton}
					/>
				)}
			</View>

			{hasSubTeams && expanded && (
				<View style={styles.subTeams}>
					{team.subTeams.map((subTeam) => (
						<TeamInfo key={subTeam.uuid} team={subTeam} activeUuid={activeUuid} />
					))}
				</View>
			)}
		</View>
	);
};

const styles = StyleSheet.create({
	flexRow: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	mainContainer: {
		padding: spacing(),
	},
	title: {
		marginLeft: spacing(),
	},
	expandButton: { marginHorizontal: spacing() },
	subTeams: {
		marginLeft: spacing(0.5),
		gap: spacing(),
		padding: spacing(),
	},
	active: {
		backgroundColor: colors.primary200,
		borderRadius: 8,
	},
});
